import { notify } from "components/ui/toaster";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useMemo, useState } from "react";
import { Agent, agentService } from "services/agentService";
import { authService } from "services/authService";
import {
  integrationService,
  ProviderInbox,
  SupportIntegration,
} from "services/integrationService";
import {
  NotificationChannel,
  notificationService,
  WidgetChannelLinkResult,
} from "services/notificationService";
import { ServerConfig, serverService } from "services/serverService";
import { Skill, skillService } from "services/skillService";
import {
  ChatLabels,
  ShortcutCard,
  WelcomeScreenTextTranslation,
  WidgetConfig,
  widgetService,
} from "services/widgetService";
import {
  AGENT_DESCRIPTIONS,
  DEFAULT_STYLE_OVERRIDES,
  STYLE_OVERRIDE_KEY_MAP,
} from "../constants/widgetConstants";

export type StyleOverrides = typeof DEFAULT_STYLE_OVERRIDES;

export function useWidgetEditor(widgetId: string | undefined) {
  const { t } = useTranslation();

  const [widget, setWidget] = useState<WidgetConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [servers, setServers] = useState<ServerConfig[]>([]);
  const [selectedServerIds, setSelectedServerIds] = useState<string[]>([]);

  // Agent state
  const [agents, setAgents] = useState<Agent[]>([]);
  const [sharedDraft, setSharedDraft] = useState({
    toneOfVoice: "",
    behaviorInstructions: "",
    brandingInstructions: "",
  });
  const [customDrafts, setCustomDrafts] = useState<Record<string, string>>({});
  const [agentsSaving, setAgentsSaving] = useState(false);
  const [agentError, setAgentError] = useState("");
  const [agentsLoaded, setAgentsLoaded] = useState(false);
  const [agentInitializing, setAgentInitializing] = useState(false);
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);

  // Skill state
  const [allSkills, setAllSkills] = useState<Skill[]>([]);
  const [widgetSkillIds, setWidgetSkillIds] = useState<string[]>([]);
  const [skillToggling, setSkillToggling] = useState<string | null>(null);

  // Style overrides
  const [styleOverridesEnabled, setStyleOverridesEnabled] = useState(false);
  const [styleOverrides, setStyleOverrides] = useState<StyleOverrides>({
    ...DEFAULT_STYLE_OVERRIDES,
  });

  // Notification state
  const [allNotificationChannels, setAllNotificationChannels] = useState<
    NotificationChannel[]
  >([]);
  const [notificationEnabled, setNotificationEnabled] = useState(false);
  const [notificationChannelLinks, setNotificationChannelLinks] = useState<
    WidgetChannelLinkResult[]
  >([]);
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [notificationError, setNotificationError] = useState("");

  // Customer authentication (P2/P3) — per-widget toggle. When true, the widget
  // forces the PIN/Password popup before mounting the chat. Server-authoritative —
  // the server-side value wins over any window.chatWidgetConfig opt-in flag.
  const [requiresEndUserAuth, setRequiresEndUserAuth] = useState(false);

  // Shortcut cards shown on the widget welcome screen
  const [shortcutCards, setShortcutCards] = useState<ShortcutCard[]>([]);

  // Welcome screen text shown above the shortcut cards, plus its per-language overrides
  const [welcomeScreenText, setWelcomeScreenText] = useState<string>("");
  const [welcomeScreenTextTranslations, setWelcomeScreenTextTranslations] =
    useState<Record<string, WelcomeScreenTextTranslation>>({});

  // Chat labels ("Verified" badge, invite-agent buttons), plus their per-language overrides
  const [chatLabels, setChatLabels] = useState<ChatLabels>({});

  // Customer Portal toggles — master + 6 per-page flags. Default true so a
  // freshly-loaded widget without a backend response keeps the full Portal
  // visible (the server response overwrites once it arrives).
  const [portalEnabled, setPortalEnabled] = useState(true);
  const [portalMembersEnabled, setPortalMembersEnabled] = useState(true);
  const [portalKnowledgeBaseEnabled, setPortalKnowledgeBaseEnabled] =
    useState(true);
  const [portalSkillsEnabled, setPortalSkillsEnabled] = useState(true);
  const [portalWorkflowsEnabled, setPortalWorkflowsEnabled] = useState(true);
  const [portalIntegrationsEnabled, setPortalIntegrationsEnabled] =
    useState(true);
  const [portalUsageEnabled, setPortalUsageEnabled] = useState(true);
  const [portalSettingsEnabled, setPortalSettingsEnabled] = useState(true);
  const [portalPromptsEnabled, setPortalPromptsEnabled] = useState(true);
  const [promptShortcutsEnabled, setPromptShortcutsEnabled] = useState(true);

  // Handoff state
  const [handoffEnabled, setHandoffEnabled] = useState(false);
  const [handoffLevel, setHandoffLevel] = useState<number>(1);
  const [handoffIntegrationId, setHandoffIntegrationId] = useState<string>("");
  const [handoffInboxId, setHandoffInboxId] = useState<string>("");
  const [handoffInboxName, setHandoffInboxName] = useState<string>("");
  const [integrations, setIntegrations] = useState<SupportIntegration[]>([]);
  const [inboxes, setInboxes] = useState<ProviderInbox[]>([]);
  const [handoffSaving, setHandoffSaving] = useState(false);
  const [handoffError, setHandoffError] = useState("");
  const [inboxesLoading, setInboxesLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    apiUrl: "",
    buttonPosition: "bottom-right",
    buttonColor: "#ffffff",
    buttonBackgroundColor: "#2563eb",
    buttonSize: "3.5rem",
    buttonMargin: "1rem",
    iframeWidth: "400px",
    iframeHeight: "600px",
    displayType: "sidebar",
    displayBehaviour: "overlay",
    modalBackdrop: true,
    svgIcon: "",
    customHeaders: "",
    aiProvider: "OpenAI",
    aiModel: "gpt-5.2",
    enableFeedback: false,
    includeUrls: "",
    excludeUrls: "",
    appPageUrl: "",
    customFonts: {
      bodyFamily: "",
      bodyUrl: "",
      headingFamily: "",
      headingUrl: "",
    },
  });

  // Feature check
  const hasHandoffFeature =
    authService
      .getCurrentUser()
      ?.company?.features?.includes("handoffIntegrations") ?? false;

  // ── Data loading ──────────────────────────────────────────────────────────

  const parseStyleOverrides = (raw?: Record<string, string> | string) => {
    if (!raw) return null;
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object"
          ? (parsed as Record<string, string>)
          : null;
      } catch {
        return null;
      }
    }
    return raw;
  };

  const applyStyleOverridesFromConfig = (
    raw?: Record<string, string> | string,
  ) => {
    const overrides = parseStyleOverrides(raw);
    if (!overrides) {
      setStyleOverrides({ ...DEFAULT_STYLE_OVERRIDES });
      setStyleOverridesEnabled(false);
      return;
    }
    const next = { ...DEFAULT_STYLE_OVERRIDES };
    Object.entries(overrides).forEach(([key, value]) => {
      const mappedKey = STYLE_OVERRIDE_KEY_MAP[key];
      if (mappedKey) next[mappedKey] = value;
    });
    setStyleOverrides(next);
    setStyleOverridesEnabled(Object.keys(overrides).length > 0);
  };

  useEffect(() => {
    if (!widgetId) {
      setWidget(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadAll = async () => {
      setLoading(true);
      setError("");
      setAgentsLoaded(false);

      try {
        const [
          widgetData,
          serverData,
          agentData,
          skills,
          widgetSkills,
          integrationsData,
          allChannels,
          notifSettings,
        ] = await Promise.all([
          widgetService.getWidgetById(widgetId),
          serverService.getAllServers(),
          agentService.getAgents(widgetId),
          skillService.getSkills(),
          skillService.getWidgetSkills(widgetId),
          integrationService.listIntegrations(),
          notificationService.listChannels(),
          notificationService.getWidgetNotificationSettings(widgetId),
        ]);

        if (cancelled) return;

        // Widget
        setWidget(widgetData);
        if (widgetData.serverIds?.length)
          setSelectedServerIds(widgetData.serverIds);

        setFormData({
          name: widgetData.name || "",
          description: widgetData.description || "",
          apiUrl: widgetData.apiUrl || "",
          buttonPosition: widgetData.buttonPosition || "bottom-right",
          buttonColor: widgetData.buttonColor || "#ffffff",
          buttonBackgroundColor: widgetData.buttonBackgroundColor || "#2563eb",
          buttonSize: widgetData.buttonSize || "3.5rem",
          buttonMargin: widgetData.buttonMargin || "1rem",
          iframeWidth: widgetData.iframeWidth || "400px",
          iframeHeight: widgetData.iframeHeight || "600px",
          displayType: widgetData.displayType || "sidebar",
          displayBehaviour: widgetData.displayBehaviour || "overlay",
          modalBackdrop: widgetData.modalBackdrop ?? true,
          svgIcon: widgetData.svgIcon || "",
          customHeaders: widgetData.customHeaders || "",
          aiProvider: widgetData.aiProvider || "OpenAI",
          aiModel: widgetData.aiModel || "gpt-5.2",
          enableFeedback: widgetData.enableFeedback ?? false,
          includeUrls: widgetData.includeUrls || "",
          excludeUrls: widgetData.excludeUrls || "",
          appPageUrl: widgetData.appPageUrl || "",
          customFonts: {
            bodyFamily: widgetData.customFonts?.bodyFamily || "",
            bodyUrl: widgetData.customFonts?.bodyUrl || "",
            headingFamily: widgetData.customFonts?.headingFamily || "",
            headingUrl: widgetData.customFonts?.headingUrl || "",
          },
        });

        applyStyleOverridesFromConfig(widgetData.styleOverrides);

        setRequiresEndUserAuth(widgetData.requiresEndUserAuth ?? false);
        setPortalEnabled(widgetData.portalEnabled ?? true);
        setPortalMembersEnabled(widgetData.portalMembersEnabled ?? true);
        setPortalKnowledgeBaseEnabled(
          widgetData.portalKnowledgeBaseEnabled ?? true,
        );
        setPortalSkillsEnabled(widgetData.portalSkillsEnabled ?? true);
        setPortalWorkflowsEnabled(widgetData.portalWorkflowsEnabled ?? true);
        setPortalIntegrationsEnabled(
          widgetData.portalIntegrationsEnabled ?? true,
        );
        setPortalUsageEnabled(widgetData.portalUsageEnabled ?? true);
        setPortalSettingsEnabled(widgetData.portalSettingsEnabled ?? true);
        setPortalPromptsEnabled(widgetData.portalPromptsEnabled ?? true);
        setPromptShortcutsEnabled(widgetData.promptShortcutsEnabled ?? true);
        setShortcutCards(widgetData.shortcutCards ?? []);
        setWelcomeScreenText(widgetData.welcomeScreenText ?? "");
        setWelcomeScreenTextTranslations(
          widgetData.welcomeScreenTextTranslations ?? {},
        );
        setChatLabels(widgetData.chatLabels ?? {});

        setHandoffEnabled(widgetData.handoffEnabled ?? false);
        setHandoffLevel(widgetData.handoffLevel ?? 1);
        setHandoffIntegrationId(widgetData.handoffSupportIntegrationId ?? "");
        setHandoffInboxId(widgetData.handoffInboxId ?? "");
        setHandoffInboxName(widgetData.handoffInboxName ?? "");

        setAllNotificationChannels(allChannels);
        setNotificationEnabled(notifSettings.notificationEnabled);
        setNotificationChannelLinks(notifSettings.channelLinks);

        // Servers
        setServers(serverData);

        // Agents
        setAgents(agentData);
        if (agentData[0]) {
          setSharedDraft({
            toneOfVoice: agentData[0].toneOfVoice ?? "",
            behaviorInstructions: agentData[0].behaviorInstructions ?? "",
            brandingInstructions: agentData[0].brandingInstructions ?? "",
          });
        }
        const customs: Record<string, string> = {};
        agentData.forEach((a) => {
          customs[a.id] = a.customInstructions ?? "";
        });
        setCustomDrafts(customs);

        // Skills assignment
        setAllSkills(skills);
        setWidgetSkillIds(widgetSkills.map((ws) => ws.skillId));

        // Integrations
        setIntegrations(integrationsData);
      } catch (err: any) {
        if (!cancelled) {
          setError(
            err.response?.data?.error ||
              err.response?.data?.message ||
              t("app.widgets.loadDetailsFailed"),
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setAgentsLoaded(true);
        }
      }
    };

    loadAll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetId]);

  // Fetch inboxes when integration changes
  useEffect(() => {
    if (!handoffIntegrationId) return;
    let cancelled = false;
    const fetchInboxes = async () => {
      try {
        setInboxesLoading(true);
        const data = await integrationService.listInboxes(handoffIntegrationId);
        if (!cancelled) setInboxes(data);
      } catch {
        // non-critical
      } finally {
        if (!cancelled) setInboxesLoading(false);
      }
    };
    fetchInboxes();
    return () => {
      cancelled = true;
    };
  }, [handoffIntegrationId]);

  // ── Style override helpers ────────────────────────────────────────────────

  const updateStyleOverride = (key: keyof StyleOverrides, value: string) => {
    setStyleOverrides((prev) => ({ ...prev, [key]: value }));
  };

  const builtStyleOverrides = useMemo(() => {
    if (!styleOverridesEnabled) return undefined;
    const overrides: Record<string, string | undefined> = {
      "--widget-primary-color-light":
        styleOverrides.primaryColorLight !==
        DEFAULT_STYLE_OVERRIDES.primaryColorLight
          ? styleOverrides.primaryColorLight
          : undefined,
      "--widget-primary-color-dark":
        styleOverrides.primaryColorDark !==
        DEFAULT_STYLE_OVERRIDES.primaryColorDark
          ? styleOverrides.primaryColorDark
          : undefined,
      "--widget-send-button-hover-light":
        styleOverrides.sendButtonHoverLight !==
        DEFAULT_STYLE_OVERRIDES.sendButtonHoverLight
          ? styleOverrides.sendButtonHoverLight
          : undefined,
      "--widget-send-button-hover-dark":
        styleOverrides.sendButtonHoverDark !==
        DEFAULT_STYLE_OVERRIDES.sendButtonHoverDark
          ? styleOverrides.sendButtonHoverDark
          : undefined,
      "--widget-secondary-color-light":
        styleOverrides.secondaryColorLight !==
        DEFAULT_STYLE_OVERRIDES.secondaryColorLight
          ? styleOverrides.secondaryColorLight
          : undefined,
      "--widget-secondary-color-dark":
        styleOverrides.secondaryColorDark !==
        DEFAULT_STYLE_OVERRIDES.secondaryColorDark
          ? styleOverrides.secondaryColorDark
          : undefined,
      "--widget-page-bg-light":
        styleOverrides.pageBgLight !== DEFAULT_STYLE_OVERRIDES.pageBgLight
          ? styleOverrides.pageBgLight
          : undefined,
      "--widget-page-bg-dark":
        styleOverrides.pageBgDark !== DEFAULT_STYLE_OVERRIDES.pageBgDark
          ? styleOverrides.pageBgDark
          : undefined,
      "--widget-page-text-light":
        styleOverrides.pageTextLight !== DEFAULT_STYLE_OVERRIDES.pageTextLight
          ? styleOverrides.pageTextLight
          : undefined,
      "--widget-page-text-dark":
        styleOverrides.pageTextDark !== DEFAULT_STYLE_OVERRIDES.pageTextDark
          ? styleOverrides.pageTextDark
          : undefined,
      "--widget-sidebar-bg-light":
        styleOverrides.sidebarBgLight !== DEFAULT_STYLE_OVERRIDES.sidebarBgLight
          ? styleOverrides.sidebarBgLight
          : undefined,
      "--widget-sidebar-bg-dark":
        styleOverrides.sidebarBgDark !== DEFAULT_STYLE_OVERRIDES.sidebarBgDark
          ? styleOverrides.sidebarBgDark
          : undefined,
      "--widget-sidebar-text-light":
        styleOverrides.sidebarTextLight !==
        DEFAULT_STYLE_OVERRIDES.sidebarTextLight
          ? styleOverrides.sidebarTextLight
          : undefined,
      "--widget-sidebar-text-dark":
        styleOverrides.sidebarTextDark !==
        DEFAULT_STYLE_OVERRIDES.sidebarTextDark
          ? styleOverrides.sidebarTextDark
          : undefined,
      "--widget-input-bg-light":
        styleOverrides.inputBgLight !== DEFAULT_STYLE_OVERRIDES.inputBgLight
          ? styleOverrides.inputBgLight
          : undefined,
      "--widget-input-bg-dark":
        styleOverrides.inputBgDark !== DEFAULT_STYLE_OVERRIDES.inputBgDark
          ? styleOverrides.inputBgDark
          : undefined,
      "--widget-input-text-light":
        styleOverrides.inputTextLight !== DEFAULT_STYLE_OVERRIDES.inputTextLight
          ? styleOverrides.inputTextLight
          : undefined,
      "--widget-input-text-dark":
        styleOverrides.inputTextDark !== DEFAULT_STYLE_OVERRIDES.inputTextDark
          ? styleOverrides.inputTextDark
          : undefined,
      "--widget-message-user-bg-light":
        styleOverrides.messageUserBgLight !==
        DEFAULT_STYLE_OVERRIDES.messageUserBgLight
          ? styleOverrides.messageUserBgLight
          : undefined,
      "--widget-message-user-bg-dark":
        styleOverrides.messageUserBgDark !==
        DEFAULT_STYLE_OVERRIDES.messageUserBgDark
          ? styleOverrides.messageUserBgDark
          : undefined,
      "--widget-message-user-text-light":
        styleOverrides.messageUserTextLight !==
        DEFAULT_STYLE_OVERRIDES.messageUserTextLight
          ? styleOverrides.messageUserTextLight
          : undefined,
      "--widget-message-user-text-dark":
        styleOverrides.messageUserTextDark !==
        DEFAULT_STYLE_OVERRIDES.messageUserTextDark
          ? styleOverrides.messageUserTextDark
          : undefined,
      "--widget-message-assistant-text-light":
        styleOverrides.messageAssistantTextLight !==
        DEFAULT_STYLE_OVERRIDES.messageAssistantTextLight
          ? styleOverrides.messageAssistantTextLight
          : undefined,
      "--widget-message-assistant-text-dark":
        styleOverrides.messageAssistantTextDark !==
        DEFAULT_STYLE_OVERRIDES.messageAssistantTextDark
          ? styleOverrides.messageAssistantTextDark
          : undefined,
      "--widget-chart-color-1":
        styleOverrides.chartColor1 !== DEFAULT_STYLE_OVERRIDES.chartColor1
          ? styleOverrides.chartColor1
          : undefined,
      "--widget-chart-color-2":
        styleOverrides.chartColor2 !== DEFAULT_STYLE_OVERRIDES.chartColor2
          ? styleOverrides.chartColor2
          : undefined,
      "--widget-chart-color-3":
        styleOverrides.chartColor3 !== DEFAULT_STYLE_OVERRIDES.chartColor3
          ? styleOverrides.chartColor3
          : undefined,
      "--widget-chart-color-4":
        styleOverrides.chartColor4 !== DEFAULT_STYLE_OVERRIDES.chartColor4
          ? styleOverrides.chartColor4
          : undefined,
      "--widget-chart-color-5":
        styleOverrides.chartColor5 !== DEFAULT_STYLE_OVERRIDES.chartColor5
          ? styleOverrides.chartColor5
          : undefined,
      "--widget-chart-color-6":
        styleOverrides.chartColor6 !== DEFAULT_STYLE_OVERRIDES.chartColor6
          ? styleOverrides.chartColor6
          : undefined,
      "--widget-chart-color-7":
        styleOverrides.chartColor7 !== DEFAULT_STYLE_OVERRIDES.chartColor7
          ? styleOverrides.chartColor7
          : undefined,
      "--widget-chart-color-8":
        styleOverrides.chartColor8 !== DEFAULT_STYLE_OVERRIDES.chartColor8
          ? styleOverrides.chartColor8
          : undefined,
      "--widget-chart-color-9":
        styleOverrides.chartColor9 !== DEFAULT_STYLE_OVERRIDES.chartColor9
          ? styleOverrides.chartColor9
          : undefined,
      "--widget-chart-color-10":
        styleOverrides.chartColor10 !== DEFAULT_STYLE_OVERRIDES.chartColor10
          ? styleOverrides.chartColor10
          : undefined,
      "--widget-message-pending-bg-light":
        styleOverrides.secondaryColorLight !==
        DEFAULT_STYLE_OVERRIDES.secondaryColorLight
          ? styleOverrides.secondaryColorLight
          : undefined,
      "--widget-message-pending-bg-dark":
        styleOverrides.secondaryColorDark !==
        DEFAULT_STYLE_OVERRIDES.secondaryColorDark
          ? styleOverrides.secondaryColorDark
          : undefined,
      "--widget-border-radius":
        styleOverrides.borderRadius !== DEFAULT_STYLE_OVERRIDES.borderRadius
          ? styleOverrides.borderRadius
          : undefined,
      "--widget-iframe-radius":
        styleOverrides.iframeCornerRadius !==
        DEFAULT_STYLE_OVERRIDES.iframeCornerRadius
          ? styleOverrides.iframeCornerRadius
          : undefined,
      "--widget-border-width":
        styleOverrides.borderWidth !== DEFAULT_STYLE_OVERRIDES.borderWidth
          ? styleOverrides.borderWidth
          : undefined,
      "--widget-border-style":
        styleOverrides.borderStyle !== DEFAULT_STYLE_OVERRIDES.borderStyle
          ? styleOverrides.borderStyle
          : undefined,
      "--widget-border-color-light":
        styleOverrides.borderColorLight !==
        DEFAULT_STYLE_OVERRIDES.borderColorLight
          ? styleOverrides.borderColorLight
          : undefined,
      "--widget-border-color-dark":
        styleOverrides.borderColorDark !==
        DEFAULT_STYLE_OVERRIDES.borderColorDark
          ? styleOverrides.borderColorDark
          : undefined,
      "--widget-input-border-width":
        styleOverrides.borderWidth !== DEFAULT_STYLE_OVERRIDES.borderWidth
          ? styleOverrides.borderWidth
          : undefined,
      "--widget-input-border-style":
        styleOverrides.borderStyle !== DEFAULT_STYLE_OVERRIDES.borderStyle
          ? styleOverrides.borderStyle
          : undefined,
      "--widget-input-border-color-light":
        styleOverrides.borderColorLight !==
        DEFAULT_STYLE_OVERRIDES.borderColorLight
          ? styleOverrides.borderColorLight
          : undefined,
      "--widget-input-border-color-dark":
        styleOverrides.borderColorDark !==
        DEFAULT_STYLE_OVERRIDES.borderColorDark
          ? styleOverrides.borderColorDark
          : undefined,
    };
    const filtered = Object.fromEntries(
      Object.entries(overrides).filter(
        ([, v]) => v !== undefined && v !== null && v !== "",
      ),
    );
    return Object.keys(filtered).length > 0 ? filtered : undefined;
  }, [styleOverridesEnabled, styleOverrides]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleSave = async (): Promise<boolean> => {
    if (!widgetId) return false;
    try {
      setSaving(true);
      const dataToSave = {
        ...formData,
        serverIds: selectedServerIds.length > 0 ? selectedServerIds : undefined,
        requiresEndUserAuth,
        portalEnabled,
        portalMembersEnabled,
        portalKnowledgeBaseEnabled,
        portalSkillsEnabled,
        portalWorkflowsEnabled,
        portalIntegrationsEnabled,
        portalUsageEnabled,
        portalSettingsEnabled,
        portalPromptsEnabled,
        promptShortcutsEnabled,
        includeUrls: formData.includeUrls || null,
        excludeUrls: formData.excludeUrls || null,
        styleOverrides: styleOverridesEnabled
          ? (builtStyleOverrides ?? {})
          : {},
        shortcutCards: shortcutCards.map(
          (c, i): ShortcutCard => ({
            id: c.id,
            title: c.title,
            prompt: c.prompt,
            order: i,
            icon: c.icon,
            // The API syncs cards by replace-all — dropping this field here would
            // silently wipe every card's translations on the next save.
            translations: c.translations,
          }),
        ),
        welcomeScreenText: welcomeScreenText,
        welcomeScreenTextTranslations,
        chatLabels,
      };
      const updatedWidget = await widgetService.updateWidget(
        widgetId,
        dataToSave,
      );
      setWidget(updatedWidget);
      notify({ type: "success", title: t("app.widgets.saveSuccess") });
      return true;
    } catch {
      // axios response interceptor renders the error toast.
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndPreview = async () => {
    const saved = await handleSave();
    if (saved) setPreviewRefreshKey((p) => p + 1);
  };

  const handleAiAgentsSave = async () => {
    if (!widgetId) return;
    setAgentsSaving(true);
    setAgentError("");
    try {
      await handleSave();
      if (agents.length > 0) {
        const customInstructions: Record<string, string | null> = {};
        agents.forEach((a) => {
          customInstructions[a.id] = customDrafts[a.id] || null;
        });
        const updated = await agentService.bulkUpdateAgents(widgetId, {
          toneOfVoice: sharedDraft.toneOfVoice || undefined,
          behaviorInstructions: sharedDraft.behaviorInstructions || undefined,
          brandingInstructions: sharedDraft.brandingInstructions || undefined,
          customInstructions,
        });
        setAgents(updated);
      }
    } catch (err: any) {
      setAgentError(err.response?.data?.message || "Failed to save");
    } finally {
      setAgentsSaving(false);
    }
  };

  const handleSkillToggle = async (skillId: string, assign: boolean) => {
    if (!widgetId) return;
    setSkillToggling(skillId);
    try {
      if (assign) {
        await skillService.assignSkill(widgetId, skillId);
        setWidgetSkillIds((prev) => [...prev, skillId]);
      } else {
        await skillService.removeSkill(widgetId, skillId);
        setWidgetSkillIds((prev) => prev.filter((s) => s !== skillId));
      }
    } catch (err: any) {
      setAgentError(
        err.response?.data?.message || "Failed to update skill assignment",
      );
    } finally {
      setSkillToggling(null);
    }
  };

  const handleAgentInitialize = async () => {
    if (!widgetId) return;
    setAgentInitializing(true);
    setAgentError("");
    try {
      const agentData = await agentService.initializeAgents(widgetId);
      setAgents(agentData);
      const customs: Record<string, string> = {};
      agentData.forEach((a) => {
        customs[a.id] = a.customInstructions ?? "";
      });
      setCustomDrafts(customs);
    } catch (err: any) {
      setAgentError(
        err.response?.data?.message || "Failed to initialize agents",
      );
    } finally {
      setAgentInitializing(false);
    }
  };

  const handleIntegrationChange = async (integrationId: string) => {
    setHandoffIntegrationId(integrationId);
    setHandoffInboxId("");
    setHandoffInboxName("");
    setInboxes([]);
    if (!integrationId) return;
    try {
      setInboxesLoading(true);
      const data = await integrationService.listInboxes(integrationId);
      setInboxes(data);
    } catch (err: any) {
      setHandoffError(err.response?.data?.error || "Failed to load inboxes.");
    } finally {
      setInboxesLoading(false);
    }
  };

  const handoffMailboxRequired =
    integrations.find((i) => i.id === handoffIntegrationId)?.providerType ===
    "HelpScout";

  const handleHandoffSave = async () => {
    if (!widgetId) return;
    if (handoffEnabled && handoffMailboxRequired && !handoffInboxId) {
      setHandoffError(t("app.supportIntegrations.handoff.mailboxRequired"));
      return;
    }
    setHandoffError("");
    try {
      setHandoffSaving(true);
      await integrationService.updateWidgetHandoffSettings(widgetId, {
        handoffEnabled,
        handoffSupportIntegrationId: handoffIntegrationId || null,
        handoffInboxId: handoffInboxId || null,
        handoffInboxName: handoffInboxName || null,
        handoffLevel,
      });
      notify({ type: "success", title: t("app.notifications.saved") });
    } catch {
      // interceptor handles the error toast
    } finally {
      setHandoffSaving(false);
    }
  };

  const handleNotificationSave = async () => {
    if (!widgetId) return;
    try {
      setNotificationSaving(true);
      await notificationService.updateWidgetNotificationSettings(widgetId, {
        enabled: notificationEnabled,
        channelLinks: notificationChannelLinks.map((l) => ({
          notificationChannelId: l.notificationChannelId,
          isDefault: l.isDefault,
        })),
      });
      notify({ type: "success", title: t("app.notifications.saved") });
    } catch {
      // interceptor handles the error toast
    } finally {
      setNotificationSaving(false);
    }
  };

  return {
    // Data
    widget,
    loading,
    saving,
    error,
    success,
    setError,
    setSuccess,
    // Form
    formData,
    setFormData,
    // Servers
    servers,
    selectedServerIds,
    setSelectedServerIds,
    // Agents
    agents,
    sharedDraft,
    setSharedDraft,
    customDrafts,
    setCustomDrafts,
    agentsSaving,
    agentError,
    agentsLoaded,
    agentInitializing,
    previewRefreshKey,
    // Skills
    allSkills,
    widgetSkillIds,
    skillToggling,
    // Styles
    styleOverridesEnabled,
    setStyleOverridesEnabled,
    styleOverrides,
    updateStyleOverride,
    builtStyleOverrides,
    // Notifications
    allNotificationChannels,
    notificationEnabled,
    setNotificationEnabled,
    notificationChannelLinks,
    setNotificationChannelLinks,
    notificationSaving,
    notificationError,
    // Shortcut cards
    shortcutCards,
    setShortcutCards,
    welcomeScreenText,
    setWelcomeScreenText,
    welcomeScreenTextTranslations,
    setWelcomeScreenTextTranslations,
    chatLabels,
    setChatLabels,
    // Customer authentication (P2/P3)
    requiresEndUserAuth,
    setRequiresEndUserAuth,
    // Customer Portal toggles
    portalEnabled,
    setPortalEnabled,
    portalMembersEnabled,
    setPortalMembersEnabled,
    portalKnowledgeBaseEnabled,
    setPortalKnowledgeBaseEnabled,
    portalSkillsEnabled,
    setPortalSkillsEnabled,
    portalWorkflowsEnabled,
    setPortalWorkflowsEnabled,
    portalIntegrationsEnabled,
    setPortalIntegrationsEnabled,
    portalUsageEnabled,
    setPortalUsageEnabled,
    portalSettingsEnabled,
    setPortalSettingsEnabled,
    portalPromptsEnabled,
    setPortalPromptsEnabled,
    promptShortcutsEnabled,
    setPromptShortcutsEnabled,
    // Handoff
    hasHandoffFeature,
    handoffEnabled,
    setHandoffEnabled,
    handoffLevel,
    setHandoffLevel,
    handoffIntegrationId,
    handoffInboxId,
    setHandoffInboxId,
    handoffInboxName,
    setHandoffInboxName,
    handoffMailboxRequired,
    integrations,
    inboxes,
    handoffSaving,
    handoffError,
    inboxesLoading,
    // Handlers
    handleSave,
    handleSaveAndPreview,
    handleAiAgentsSave,
    handleSkillToggle,
    handleAgentInitialize,
    handleIntegrationChange,
    handleHandoffSave,
    handleNotificationSave,
    // Constants (re-exported for consumers)
    AGENT_DESCRIPTIONS,
  };
}
